import { FormEvent, MouseEvent, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { UseOpenCloseReturn } from '~/core/hooks/useOpenClose';
import {
  useDealIndividualsForm,
  useRunIndividualPlugin,
  UseRunIndividualPluginOptions,
} from '~/core/shared/api/plugin';
import { useFormResolver } from '~/core/shared/hooks';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { Option } from '~/core/shared/types';
import { IndividualPluginFormValues } from '../../types';

const DEFAULT_VALUES: IndividualPluginFormValues = {
  individualId: '',
};

// TODO: @haxxxton refactor this to use form blocks
const VALIDATION_SCHEMA = yup.object({
  individualId: yup.string().required('An applicant is required'),
});

const EMPTY_APPLICANT_OPTIONS: Option<string>[] = [];

type UseIndividualPluginFormArgs = {
  modalApi: UseOpenCloseReturn;
  uuid: string;
  runOptions: UseRunIndividualPluginOptions;
  onSuccess?: () => void;
};

export const useIndividualPluginForm = ({
  modalApi,
  uuid,
  runOptions,
  onSuccess,
}: UseIndividualPluginFormArgs) => {
  // Applicants are only fetched once the modal is open: the options are a plugin call per deal, and the action sits
  // in a menu that most visits to a deal never touch.
  const { formData, isLoading: isLoadingApplicants } = useDealIndividualsForm(
    uuid,
    { enabled: modalApi.open },
  );

  const { runIndividualPlugin, isPending } = useRunIndividualPlugin(runOptions);

  const resolver =
    useFormResolver<IndividualPluginFormValues>(VALIDATION_SCHEMA);

  const form = useForm<IndividualPluginFormValues>({
    defaultValues: DEFAULT_VALUES,
    resolver,
  });

  useEffect(() => {
    if (modalApi.open) {
      form.reset(DEFAULT_VALUES);
    }
  }, [modalApi.open]);

  const applicantOptions = useMemo(
    () =>
      formData?.individuals.map(({ label, value }) => ({ label, value })) ??
      EMPTY_APPLICANT_OPTIONS,
    [formData],
  );

  const handleSubmit = useEventHandler(
    async ({ individualId }: IndividualPluginFormValues): Promise<void> => {
      try {
        await runIndividualPlugin({
          params: { individualId, uuid },
        });

        onSuccess?.();
        modalApi.onClose();
      } catch {
        // error handled in useRunIndividualPlugin
      }
    },
  );

  const onSubmit = useEventHandler(
    (event: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      form.handleSubmit(handleSubmit)(event);
    },
  );

  return {
    applicantOptions,
    form,
    isLoadingApplicants,
    isSubmitting: isPending,
    onSubmit,
  };
};
